import { SchedulingFilter, DbRef, orderedDaysOfWeek } from '@caresend/types';
import { getDistanceBetweenLocations } from './location.js';
import { getValueOnce } from '../environment/data/privateHelpers.js';
import { deduplicateArray } from '../languageExtensions/array.js';
import 'lodash-unified';

const isMobileCollectionEnabled = schedulingFilters => schedulingFilters.includes(SchedulingFilter.MOBILE_COLLECTION);
const isNurseFilterEnabled = schedulingFilters => schedulingFilters.includes(SchedulingFilter.NURSE);
const isCareSendSupplyFilterEnabled = schedulingFilters => schedulingFilters.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_CARESEND);
const isOfficeSupplyFilterEnabled = schedulingFilters => schedulingFilters.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_OFFICE);
const isDropOffFilterEnabled = schedulingFilters => schedulingFilters.includes(SchedulingFilter.DROP_OFF);
const isSiteCollectionFilterEnabled = (schedulingFilters, schedulingConfig) => {
  const siteCollectionFilterPresent = schedulingFilters.includes(SchedulingFilter.SITE_COLLECTION);
  const siteCollectionEnabledByOffice = schedulingConfig.dropOffScheduling.labDrawSiteCollection;
  return siteCollectionFilterPresent && siteCollectionEnabledByOffice;
};
const getTimeSlotsByServiceRegion = async serviceRegionID => getValueOnce(`${DbRef.TIME_SLOTS_BY_SERVICE_REGION}/${serviceRegionID}`);
/**
 * Return the patient supply owner info by ownerID by timestamp
 * given a service region group
 */

const getPatientShipmentInfoOnServiceRegionGroup = async serviceRegionGroupID => getValueOnce(`${DbRef.PATIENT_SHIPMENT_INFO_BY_SERVICE_REGION_GROUP}/${serviceRegionGroupID}`);
/**
 * Return the lab supply shipment info by place group ID by timestamp.
 */

const getLabShipmentInfoOnServiceRegionGroup = async serviceRegionGroupID => getValueOnce(`${DbRef.LAB_SHIPMENT_INFO_BY_SERVICE_REGION_GROUP}/${serviceRegionGroupID}`);
const getPlaceInfoByServiceRegion = async serviceRegionID => getValueOnce(`${DbRef.PLACES_BY_SERVICE_REGION}/${serviceRegionID}`);
/**
 * Get the scheduling filter layer based on the supply instruction config options
 * by procedure. Return a single SchedulingFilter array with the filters that are
 * enabled. Each filter is enabled if the supply instruction config options
 * includes the filter, except for `SITE_COLLECTION` and `MOBILE_COLLECTION`, which
 * are only included if all the procedures have it enabled.
 *
 * However, if the `isNurseRescheduling` flag is true, we return a filter with the
 * `MOBILE_COLLECTION` and optional `DROP_OFF` filters.
 */

const getSchedulingFilters = (isNurseRescheduling, supplyInstructionConfigOptions) => {
  if (isNurseRescheduling) {
    const someInstructionHasDropOff = Object.values(supplyInstructionConfigOptions !== null && supplyInstructionConfigOptions !== void 0 ? supplyInstructionConfigOptions : {}).some(instructions => {
      var _a;

      return (_a = instructions.schedulingFilters) === null || _a === void 0 ? void 0 : _a.includes(SchedulingFilter.DROP_OFF);
    });
    const filters = [SchedulingFilter.MOBILE_COLLECTION];
    if (someInstructionHasDropOff) filters.push(SchedulingFilter.DROP_OFF);
    return filters;
  }

  const schedulingFilter = [];
  let includeSiteCollection = true;
  let includeMobileCollection = true;
  Object.entries(supplyInstructionConfigOptions !== null && supplyInstructionConfigOptions !== void 0 ? supplyInstructionConfigOptions : {}).forEach(([_, instructions]) => {
    var _a;

    const layer = Object.values((_a = instructions.schedulingFilters) !== null && _a !== void 0 ? _a : {});

    if (Array.isArray(layer)) {
      if (layer.includes(SchedulingFilter.SUPPLY) || layer.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_CARESEND)) {
        schedulingFilter.push(SchedulingFilter.SUPPLIES_SHIPPED_BY_CARESEND);
      }

      if (layer.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_OFFICE)) {
        schedulingFilter.push(SchedulingFilter.SUPPLIES_SHIPPED_BY_OFFICE);
      }

      if (layer.includes(SchedulingFilter.NURSE)) {
        schedulingFilter.push(SchedulingFilter.NURSE);
      }

      if (layer.includes(SchedulingFilter.DROP_OFF)) {
        schedulingFilter.push(SchedulingFilter.DROP_OFF);
      }

      if (!layer.includes(SchedulingFilter.SITE_COLLECTION)) {
        includeSiteCollection = false;
      }

      if (!layer.includes(SchedulingFilter.MOBILE_COLLECTION)) {
        includeMobileCollection = false;
      }
    }
  });

  if (includeSiteCollection) {
    schedulingFilter.push(SchedulingFilter.SITE_COLLECTION);
  }

  if (includeMobileCollection) {
    schedulingFilter.push(SchedulingFilter.MOBILE_COLLECTION);
  }

  return deduplicateArray(schedulingFilter);
};
/**
 * Helper function to adjust opening hours for a single day based on time slot restrictions.
 *
 * @param openingHour - The opening hour to adjust for a single day
 * @param restriction - The time restriction for this specific day (if any)
 * @returns The adjusted opening hour, or null if the day should be filtered out entirely
 */

const adjustSingleDayOpeningHours = (openingHour, restriction) => {
  // No restriction for this day, keep original hours
  if (!restriction) return openingHour;
  const openTime = Number(openingHour.timeFrom);
  const closeTime = Number(openingHour.timeTo); // Skip this day entirely if it opens after the restriction end time

  const isAfterRestrictionEndTime = openTime >= restriction.endTime;
  if (isAfterRestrictionEndTime) return null;
  const adjustedOpenTime = openTime < restriction.startTime ? String(restriction.startTime) : openingHour.timeFrom;
  const adjustedCloseTime = closeTime > restriction.endTime ? String(restriction.endTime) : openingHour.timeTo;
  return Object.assign(Object.assign({}, openingHour), {
    timeFrom: adjustedOpenTime,
    timeTo: adjustedCloseTime
  });
};
/**
 * Adjust the opening hours for all days of the week based on time slot restrictions.
 * For each day that has a restriction, this function will:
 * - Adjust the closing time if it exceeds the restriction end time
 * - Filter out the day entirely if the opening time is after the restriction end time
 * - Apply special start time restrictions if the opening time is before the restriction start time
 *
 * @param openingHours - The original opening hours to adjust. Assumed to not have any 'overflowing' day ranges.
 * @param timeSlotRestrictions - Time restrictions for each day of the week
 * @returns The adjusted opening hours with restrictions applied
 */


const maybeAdjustOpeningHoursForAllDays = (openingHours, timeSlotRestrictions) => {
  const adjustedOpeningHours = [];
  openingHours.forEach(openingHour => {
    const dayFromIndex = orderedDaysOfWeek.indexOf(openingHour.dayFrom);
    const dayToIndex = orderedDaysOfWeek.indexOf(openingHour.dayTo); // For single day ranges

    if (dayFromIndex === dayToIndex) {
      const day = openingHour.dayFrom;
      const restriction = timeSlotRestrictions[day];
      const adjustedHour = adjustSingleDayOpeningHours(openingHour, restriction);
      if (adjustedHour) adjustedOpeningHours.push(adjustedHour);
      return;
    } // For multi-day ranges, split them and handle each day separately


    let currentRange = null;

    for (let i = dayFromIndex; i <= dayToIndex; i++) {
      const currentDay = orderedDaysOfWeek[i];
      if (!currentDay) continue; // Won't happen with valid input

      const restriction = timeSlotRestrictions[currentDay]; // Create a single day opening hour to use with our helper function

      const singleDayHour = {
        dayFrom: currentDay,
        dayTo: currentDay,
        timeFrom: openingHour.timeFrom,
        timeTo: openingHour.timeTo
      };
      const adjustedHour = adjustSingleDayOpeningHours(singleDayHour, restriction); // This day was filtered out, push any accumulated range and continue

      if (!adjustedHour) {
        if (currentRange) {
          adjustedOpeningHours.push(currentRange);
          currentRange = null;
        }

        continue;
      }

      if (!restriction) {
        if (!currentRange) {
          currentRange = adjustedHour;
        } else {
          currentRange.dayTo = currentDay;
        }
      } else {
        // There's a restriction for this day, push any accumulated range first
        if (currentRange) {
          adjustedOpeningHours.push(currentRange);
          currentRange = null;
        }

        adjustedOpeningHours.push(adjustedHour);
      }
    }

    if (currentRange) adjustedOpeningHours.push(currentRange);
  });
  return adjustedOpeningHours;
};
const getBrickAndMortarLab = (place, placeGroupName, placePhoneNumber, draftWaypointLocation, integration) => {
  var _a;

  let distanceToAddress = 0;

  if (place.location && draftWaypointLocation) {
    distanceToAddress = getDistanceBetweenLocations(place.location, draftWaypointLocation, true);
  }

  const appointmentOnly = 'appointmentOnly' in place ? place.appointmentOnly : false;
  return Object.assign(Object.assign({}, appointmentOnly && {
    appointmentOnly
  }), {
    distanceToAddress,
    location: place.location,
    integration,
    name: placeGroupName,
    openingHours: (_a = place.openingHours) !== null && _a !== void 0 ? _a : [],
    phoneNumber: placePhoneNumber,
    placeGroupID: place.placeGroupID,
    placeID: place.id,
    price: 0
  });
};

export { getBrickAndMortarLab, getLabShipmentInfoOnServiceRegionGroup, getPatientShipmentInfoOnServiceRegionGroup, getPlaceInfoByServiceRegion, getSchedulingFilters, getTimeSlotsByServiceRegion, isCareSendSupplyFilterEnabled, isDropOffFilterEnabled, isMobileCollectionEnabled, isNurseFilterEnabled, isOfficeSupplyFilterEnabled, isSiteCollectionFilterEnabled, maybeAdjustOpeningHoursForAllDays };
